import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import { Menu, X, LogIn, Handshake, Phone, Mail, MapPin, Globe, Sun, Moon } from "lucide-react";
import api from "@/lib/api";
import { SiteCtx, metinler, DILLER, waLink } from "@/lib/siteDil";
import { modOku, modUygula } from "@/lib/mod";

const WaIkon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true"><path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.6.13-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.75-.72 2-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35M12.05 21.5h-.01a9.4 9.4 0 0 1-4.8-1.32l-.34-.2-3.57.94.95-3.48-.22-.36a9.4 9.4 0 0 1-1.44-5.02c0-5.2 4.24-9.43 9.44-9.43a9.38 9.38 0 0 1 9.43 9.44c0 5.2-4.23 9.43-9.44 9.43m8.03-17.47A11.3 11.3 0 0 0 12.05.7C5.8.7.7 5.8.7 12.05c0 2 .52 3.95 1.52 5.67L.6 23.3l5.7-1.5a11.3 11.3 0 0 0 5.75 1.47c6.26 0 11.35-5.1 11.35-11.35 0-3.03-1.18-5.88-3.32-8.02" /></svg>
);

const WaDugme = ({ no, mesaj, etiket, detay }) => (
  <a href={waLink(no, mesaj)} target="_blank" rel="noopener noreferrer" aria-label={etiket} title={etiket} data-testid="site-whatsapp"
    className={`group fixed bottom-5 ${detay ? "hidden lg:flex" : "flex"} end-5 z-50 items-center gap-2 h-14 ps-4 pe-4 rounded-full bg-[#25D366] text-white shadow-lg shadow-black/20 hover:bg-[#1ebe5b] hover:-translate-y-0.5 transition-[transform,background-color]`}>
    <WaIkon className="w-7 h-7" /><span className="hidden sm:inline text-sm font-semibold">{etiket}</span>
  </a>
);
export { WaIkon };

const Logo = ({ ad, yol, logo }) => (
  <Link to={yol("")} className="flex items-center gap-2.5 shrink-0" data-testid="site-logo">
    {logo ? <img src={logo} alt={ad} className="h-11 w-auto max-w-[200px] object-contain" data-testid="site-logo-img" /> : <>
      <span className="w-9 h-9 rounded-sm bg-[var(--s-primary)] text-white flex items-center justify-center s-head font-extrabold text-lg">{(ad || "?").charAt(0).toLocaleUpperCase("tr")}</span>
      <span className="s-head font-extrabold text-lg uppercase tracking-tight text-slate-900 leading-none">{ad}</span></>}
  </Link>
);

const DilSec = ({ lang }) => {
  const loc = useLocation();
  const yeni = (k) => loc.pathname.replace(/^\/site\/(tr|en|ar)/, `/site/${k}`) + loc.search;
  return (
    <div className="flex items-center gap-0.5 text-xs" data-testid="site-dil">
      <Globe className="w-3.5 h-3.5 text-slate-400 me-1" />
      {DILLER.map((d) => (
        <Link key={d.kod} to={yeni(d.kod)} data-testid={`site-dil-${d.kod}`}
          className={`px-2 py-1 rounded-sm transition-colors ${d.kod === "ar" ? "font-[Cairo] text-sm font-semibold" : ""} ${lang === d.kod ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{d.ad}</Link>
      ))}
    </div>
  );
};

const ModDugme = ({ mod, degistir, t }) => (
  <button onClick={degistir} data-testid="site-mod-btn" aria-label={mod === "gece" ? t.gunduzModu : t.geceModu} title={mod === "gece" ? t.gunduzModu : t.geceModu}
    className="w-10 h-10 shrink-0 rounded-sm border border-slate-300 text-slate-700 hover:text-[var(--s-primary)] hover:border-[var(--s-primary)] flex items-center justify-center transition-colors">
    {mod === "gece" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
  </button>
);

const Baslik = ({ t, lang, yol, ad, logo, mod, degistir }) => {
  const [acik, setAcik] = useState(false);
  const loc = useLocation();
  useEffect(() => { setAcik(false); }, [loc.pathname]);
  const menu = [["", t.anasayfa, "anasayfa"], ["/urunler", t.urunler, "urunler"], ["/kurumsal", t.kurumsal, "kurumsal"], ["/kataloglar", t.kataloglar, "kataloglar"], ["/iletisim", t.iletisim, "iletisim"]];
  const lnk = ({ isActive }) => `text-sm font-medium transition-colors ${isActive ? "text-[var(--s-primary)]" : "text-slate-700 hover:text-[var(--s-primary)]"}`;
  const butonlar = (
    <>
      <a href="/bayi" data-testid="site-bayi-giris" className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-sm border border-slate-300 text-sm font-medium text-slate-800 hover:border-[var(--s-primary)] hover:text-[var(--s-primary)] transition-colors"><LogIn className="w-4 h-4 rtl:rotate-180" />{t.bayiGiris}</a>
      <a href="/bayi-basvuru" data-testid="site-bayi-ol" className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-sm bg-[var(--s-accent)] hover:bg-[var(--s-accent-h)] text-white text-sm font-semibold transition-colors"><Handshake className="w-4 h-4" />{t.bayiOl}</a>
    </>
  );
  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-6">
        <Logo ad={ad} yol={yol} logo={logo} />
        <nav className="hidden lg:flex items-center gap-7 ms-4">{menu.map(([p, l, k]) => <NavLink key={k} end={p === ""} to={yol(p)} className={lnk} data-testid={`site-nav-${k}`}>{l}</NavLink>)}</nav>
        <div className="hidden lg:block"><ModDugme mod={mod} degistir={degistir} t={t} /></div>
        <div className="hidden lg:flex items-center gap-3 ms-auto"><DilSec lang={lang} />{butonlar}</div>
        <div className="lg:hidden ms-auto"><ModDugme mod={mod} degistir={degistir} t={t} /></div>
        <button className="lg:hidden p-2 rounded-sm hover:bg-slate-100" onClick={() => setAcik(!acik)} aria-label={t.menu} data-testid="site-menu-ac">{acik ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
      </div>
      {acik && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-4" data-testid="site-mobil-menu">
          <nav className="flex flex-col gap-3">{menu.map(([p, l, k]) => <NavLink key={k} end={p === ""} to={yol(p)} className={lnk} data-testid={`site-mnav-${k}`}>{l}</NavLink>)}</nav>
          <DilSec lang={lang} />
          <div className="grid grid-cols-2 gap-2">{butonlar}</div>
        </div>
      )}
    </header>
  );
};

const Alt = ({ t, yol, ic }) => (
  <footer className="bg-slate-950 text-slate-300 mt-24" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid gap-10 md:grid-cols-4">
      <div className="md:col-span-2 space-y-3">
        {ic?.genel.logo_gece ? <img src={ic.genel.logo_gece} alt={ic.genel.firma_adi} className="h-16 w-auto max-w-[220px] object-contain" data-testid="site-footer-logo" />
          : <div className="s-head text-white text-xl font-extrabold uppercase">{ic?.genel.firma_adi}</div>}
        <p className="text-sm text-slate-400 max-w-md">{ic?.metin.footer_metin}</p>
      </div>
      <div className="space-y-2 text-sm">
        {[["/urunler", t.urunler], ["/kurumsal", t.kurumsal], ["/kataloglar", t.kataloglar], ["/iletisim", t.iletisim]].map(([p, l]) => <Link key={p} to={yol(p)} className="block hover:text-white transition-colors">{l}</Link>)}
        <a href="/bayi" className="block hover:text-white transition-colors">{t.bayiGiris}</a>
      </div>
      <div className="space-y-2.5 text-sm">
        <div className="flex gap-2"><Phone className="w-4 h-4 mt-0.5 shrink-0 text-[var(--s-accent)]" /><span dir="ltr">{ic?.genel.telefon}</span></div>
        <div className="flex gap-2"><Mail className="w-4 h-4 mt-0.5 shrink-0 text-[var(--s-accent)]" />{ic?.genel.eposta}</div>
        <div className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[var(--s-accent)]" />{ic?.genel.adres}</div>
      </div>
    </div>
    <div className="border-t border-white/10 py-5 pb-24 lg:pb-5 text-center text-xs text-slate-500">© {new Date().getFullYear()} {ic?.genel.firma_adi}. {t.tumHaklar} · <a href="/login" data-testid="site-personel-giris" className="hover:text-slate-300">Personel girişi</a></div>
  </footer>
);

export default function SiteLayout() {
  const { lang } = useParams();
  const [ic, setIc] = useState(null);
  const [waUrun, setWaUrun] = useState(null);
  const [mod, setMod] = useState(() => localStorage.getItem("site-mod") || "gunduz");
  const nav = useNavigate();
  const gecerli = ["tr", "en", "ar"].includes(lang);
  useEffect(() => { api.get("/site/tema").then((r) => { if (r.data.mod) { setMod(r.data.mod); localStorage.setItem("site-mod", r.data.mod); } }).catch(() => {}); }, []);
  const degistir = () => {
    const y = mod === "gece" ? "gunduz" : "gece";
    setMod(y); localStorage.setItem("site-mod", y);
    api.put("/site/tema", { mod: y }).catch(() => {});
  };
  useEffect(() => { if (gecerli) { api.get("/site/icerik", { params: { lang } }).then((r) => setIc(r.data)).catch(() => {}); } }, [lang, gecerli]);
  useEffect(() => { if (ic) { document.title = `${ic.genel.firma_adi} | ${metinler(lang).urunler}`; } }, [ic, lang]);
  useEffect(() => { modUygula("gece"); return () => { modUygula(modOku()); }; }, []);
  if (!gecerli) return <Navigate to="/site/tr" replace />;
  const t = metinler(lang);
  const yol = (p) => `/site/${lang}${p}`;
  return (
    <SiteCtx.Provider value={{ lang, t, ic, yol, nav, setWaUrun }}>
      <div className={`site-root min-h-screen flex flex-col ${mod === "gece" ? "site-gece" : ""}`} data-mod={mod} dir={lang === "ar" ? "rtl" : "ltr"} lang={lang} data-testid="site-root">
        <Baslik t={t} lang={lang} yol={yol} ad={ic?.genel.firma_adi} logo={mod === "gece" ? ic?.genel.logo_gece : ic?.genel.logo_gunduz} mod={mod} degistir={degistir} />
        <main className="flex-1"><Outlet /></main>
        <Alt t={t} yol={yol} ic={ic} />
        {ic?.genel.whatsapp?.replace(/\D/g, "") && <WaDugme no={ic.genel.whatsapp} etiket={t.waSor} detay={!!waUrun} mesaj={waUrun ? t.waUrun(waUrun.ad, waUrun.kod) : t.waGenel} />}
      </div>
    </SiteCtx.Provider>
  );
}
